import React, { useState, useEffect } from 'react'; // Import useState and useEffect
import { Form, Input, Button, Card, Typography, Alert } from 'antd'; // Import Alert
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { useAuth } from '../context/AuthContext'; // Import useAuth hook
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import LanguageToggle from '../components/LanguageToggle'; // Import LanguageToggle

const { Title } = Typography;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth(); // Get login function and auth status from context
  const { t } = useTranslation(); // Get translation function
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const onFinish = async (values) => {
    setError(null); // Clear previous errors
    setLoading(true);
    console.log('Attempting login with:', values);
    try {
      const result = await login(values.username, values.password); // Changed from email to username
      if (result.success) {
        // Redirect to dashboard on successful login
        navigate('/dashboard');
      } else {
        setError(result.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) { // Catch unexpected errors during the login call itself
       console.error('Unexpected login error:', err);
       setError('An unexpected error occurred. Please try again.');
    } finally {
       setLoading(false); // Stop loading indicator
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      position: 'relative'
    }}>
      <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 1000 }}>
        <LanguageToggle type="default" />
      </div>
      <Card
        title={<Title level={3} style={{ textAlign: 'center', margin: 0 }}>{t('auth.loginTitle')}</Title>}
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: '12px'
        }}
      >
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />} {/* Display error Alert */}
        <Form
          name="normal_login"
          className="login-form"
          initialValues={{ remember: true }}
          onFinish={onFinish}
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: t('auth.pleaseInputUsername') },
              { min: 3, message: 'Username must be at least 3 characters long' },
              { max: 30, message: 'Username must be less than 30 characters long' }
            ]}
          >
            <Input prefix={<UserOutlined className="site-form-item-icon" />} placeholder={t('auth.username')} />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: t('auth.pleaseInputPassword') }]}
          >
            <Input.Password
              prefix={<LockOutlined className="site-form-item-icon" />}
              type="password"
              placeholder={t('auth.password')}
            />
          </Form.Item>
        {/* Optional: Add Remember me checkbox or Forgot password link here */}
        {/* <Form.Item>
          <Form.Item name="remember" valuePropName="checked" noStyle>
            <Checkbox>Remember me</Checkbox>
          </Form.Item>
          <a className="login-form-forgot" href="">
            Forgot password
          </a>
        </Form.Item> */}

        <Form.Item>
          <Button type="primary" htmlType="submit" className="login-form-button" block loading={loading}> {/* Add loading state to button */}
            {t('auth.login')}
          </Button>
          {/* Optional: Add Register link here */}
          {/* Or <a href="">register now!</a> */}
        </Form.Item>
      </Form>
    </Card>
    </div>
  );
};

export default LoginPage;
